import { useEffect } from 'react';
import Button from '../../../../../Button/src/tsx/Button';
import { useBookingContext } from '../../contexts/BookingContext';
import { formatFullDate } from '../../utils/dates';
import EditAppointmentButton from '../atoms/EditAppointmentButton';
import Heading from '../atoms/Heading';
import SubmitAppointmentButton from '../atoms/SubmitAppointmentButton';
import AppointmentInfo from '../molecules/AppointmentInfo';
import Container, { Wrapper } from '../organisms/Container';
import Form from '../organisms/Form';
import Navigation from '../organisms/Navigation';

const AVAILABILITY_REFRESH_INTERVAL = 1000 * 60; // 60 seconds

interface ScheduleAppointmentProps {
	getLabel: (key: string) => string;
	onExit?: () => void;
}

export default function ScheduleAppointment({ getLabel, onExit }: ScheduleAppointmentProps) {
	const { appointment, availability, form, locale, timezone, service, setScene } = useBookingContext();

	useEffect(() => {
		setScene('schedule_appointment');
	}, []);

	// Periodically refresh availabilities
	useEffect(() => {
		// Only start interval if we have the required data
		if (!service.businessId || !service.serviceId) {
			return;
		}

		const updateInterval = setInterval(availability.fetch, AVAILABILITY_REFRESH_INTERVAL);
		return () => clearInterval(updateInterval);
	}, [availability.fetch, service.businessId, service.serviceId]);

	let appointmentInfoHeading;
	if (availability.isLoading || appointment.timeSlot) {
		appointmentInfoHeading = getLabel(appointment.timeSlotExpired ? 'appointment_info_expired' : 'appointment_info_next_available');
	} else {
		appointmentInfoHeading = getLabel('appointment_info_none_available');
	}

	let appointmentInfoTimeSlotString = '';
	if (appointment.timeSlot) {
		appointmentInfoTimeSlotString = formatFullDate(appointment.timeSlot.start_datetime, appointment.timeSlot.end_datetime, locale, timezone.current);
	}

	return (
		<Container id="schedule-appointment">
			<Wrapper>
				<Heading title={getLabel('booking_heading')} />
				<AppointmentInfo
					heading={appointmentInfoHeading}
					onClick={appointment.canBeEdited ? appointment.edit : undefined}
					isLoading={!appointment.timeSlot && availability.isLoading}
					timeSlotString={appointmentInfoTimeSlotString}
					warning={appointment.timeSlotExpired}
				>
					{appointment.canBeEdited && (
						<div className="appointment-info__change-btn">
							<EditAppointmentButton getLabel={getLabel} />
						</div>
					)}
				</AppointmentInfo>
				<Form groupedFields={form.groupedFields} getLabel={getLabel} isSubmitting={appointment.isSubmitting} updateFieldValue={form.updateFieldValue} />
			</Wrapper>
			<Navigation keyProp="schedule-appointment">
				{form.step < form.totalSteps - 1 ? (
					<Button onClick={form.nextStep} testId="nav-next">
						{getLabel('nav_next_button')}
					</Button>
				) : (
					<SubmitAppointmentButton getLabel={getLabel} />
				)}
				{form.step > 0 ? (
					<Button onClick={form.previousStep} testId="nav-back">
						{getLabel('nav_back_button')}
					</Button>
				) : onExit ? (
					<Button onClick={onExit} testId="nav-back">
						{getLabel('nav_back_button')}
					</Button>
				) : null}
			</Navigation>
		</Container>
	);
}
